import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Text, Checkbox, CardHeader } from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';
import { MessageBundle } from '@amzn/arb-tools';
import DropdownController from '../../../shared/components/form/DropdownController';
import ReadOnlyFormField from '../../../shared/components/form/ReadOnlyFormRow';
import { RegistrationFormCard } from '../../../shared/features/account-registration/style';
import { ActivatePageContext } from '../../../shared/services/activation/activation';
import {
  ADMIN_SETTINGS_HEADER_KEY,
  MANAGER_ACCOUNT_ID_LABEL_KEY,
  MANAGER_ACCOUNT_NAME_LABEL_KEY,
  ENTITY_TYPE_LABEL_KEY,
  ENTITY_TYPE_VALUE_KEY,
  TIME_ZONE_LABEL_KEY,
  TIME_ZONE_TOOLTIP_KEY,
  AASP_LABEL_KEY,
  AASP_CHECKBOX_LABEL_KEY,
  PO_NUMBER_LABEL_KEY,
  PO_NUMBER_TOOLTIP_KEY,
  PO_CHECKBOX_LABEL_KEY,
  SELLER_OF_RECORD_LABEL_KEY,
  SELLER_OF_RECORD_TOOLTIP_KEY,
  PRODUCT_TYPE_LABEL_KEY,
  SELECT_PLACEHOLDER_KEY,
  REQUIRED_ERROR_KEY,
} from '../constants';

interface AdminSettingsProps {
  intl: MessageBundle;
  context: ActivatePageContext;
}

// ProductType values from D16GAdspActivationServiceModel ProductType enum.
const PRODUCT_TYPE_ITEMS = [
  { value: 'SELF_SERVICE', label: 'STANDARD' },
  { value: 'MANAGED_SERVICE', label: 'MANAGED' },
];

// Marketplaces and SORs where Product Type is shown (legacy parity).
const PRODUCT_TYPE_MARKETPLACES = ['AE', 'BR', 'EN'];
const PRODUCT_TYPE_SELLERS_OF_RECORD = ['AE', 'BR'];

const AdminSettings: FC<AdminSettingsProps> = ({ intl, context }) => {
  const { watch, setValue } = useFormContext();
  const select = intl.getMessage(SELECT_PLACEHOLDER_KEY);
  const required = intl.getMessage(REQUIRED_ERROR_KEY);

  const marketplace = (watch('marketplace') as string) || '';
  const sellerOfRecord = (watch('globalSellerOfRecord') as string) || '';

  const timezoneItems = useMemo(
    () =>
      (context.timeZoneOptions || []).map((tz) => ({ value: tz, label: tz })),
    [context.timeZoneOptions],
  );

  const sellerOfRecordItems = useMemo(
    () =>
      Object.entries(context.sellerOfRecords || {}).map(([code, name]) => ({
        value: code,
        label: name,
      })),
    [context.sellerOfRecords],
  );

  const showProductType =
    PRODUCT_TYPE_MARKETPLACES.includes(marketplace) ||
    PRODUCT_TYPE_SELLERS_OF_RECORD.includes(sellerOfRecord);

  return (
    <RegistrationFormCard>
      <CardHeader padding="small">
        <Text data-testid="activation-settings-title" size="xlarge">
          {intl.getMessage(ADMIN_SETTINGS_HEADER_KEY)}
        </Text>
      </CardHeader>
      <Form id="admin-settings">
        <ReadOnlyFormField
          label={intl.getMessage(MANAGER_ACCOUNT_ID_LABEL_KEY)}
          id="managerAccountIdRow"
          value={context.managerAccountId}
        />
        <ReadOnlyFormField
          label={intl.getMessage(MANAGER_ACCOUNT_NAME_LABEL_KEY)}
          id="managerAccountNameRow"
          value={context.managerAccountName}
        />
        <ReadOnlyFormField
          label={intl.getMessage(ENTITY_TYPE_LABEL_KEY)}
          id="entityTypeROw"
          value={intl.getMessage(ENTITY_TYPE_VALUE_KEY)}
        />
        <DropdownController
          label={intl.getMessage(TIME_ZONE_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="timezone-row"
          controllerName="timezone"
          items={timezoneItems}
          helpTipText={intl.getMessage(TIME_ZONE_TOOLTIP_KEY)}
          registerOptions={{ required }}
        />
        <ReadOnlyFormField
          label={intl.getMessage(AASP_LABEL_KEY)}
          id="amzn-ad-sales-partner"
          labelTopSpacing="0px"
        >
          <Checkbox
            label={intl.getMessage(AASP_CHECKBOX_LABEL_KEY)}
            checked={watch('amznAdSalesPartner')}
            onChange={(e) =>
              setValue(
                'amznAdSalesPartner',
                (e.target as HTMLInputElement).checked,
              )
            }
          />
        </ReadOnlyFormField>
        <ReadOnlyFormField
          label={intl.getMessage(PO_NUMBER_LABEL_KEY)}
          id="require-po-number"
          helpTipText={intl.getMessage(PO_NUMBER_TOOLTIP_KEY)}
        >
          <Checkbox
            label={intl.getMessage(PO_CHECKBOX_LABEL_KEY)}
            checked={watch('requirePoNumber')}
            onChange={(e) =>
              setValue(
                'requirePoNumber',
                (e.target as HTMLInputElement).checked,
              )
            }
          />
        </ReadOnlyFormField>
        <DropdownController
          label={intl.getMessage(SELLER_OF_RECORD_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="seller-of-record-input"
          controllerName="globalSellerOfRecord"
          items={sellerOfRecordItems}
          helpTipText={intl.getMessage(SELLER_OF_RECORD_TOOLTIP_KEY)}
          registerOptions={{ required }}
        />
        {showProductType && (
          <DropdownController
            label={intl.getMessage(PRODUCT_TYPE_LABEL_KEY)}
            overrideDropdownLabel={select}
            id="product-type-input"
            controllerName="productType"
            items={PRODUCT_TYPE_ITEMS}
          />
        )}
      </Form>
    </RegistrationFormCard>
  );
};

export default AdminSettings;
